import Layout from '@/layouts/_layout';
import { NextPageWithLayout } from '@/types';
import { PricingLayout } from '@/components/billing/pricing-layout';
import { useMe } from '@/data/user';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import Client from '@/data/client';
import { X, CreditCard, RefreshCw } from 'lucide-react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import Pbucks from '@/assets/images/P-Bucks.svg';
import { DiamondIcon } from '@/components/icons/diamond-icon';
import { useRouter } from 'next/router';

const Billing: NextPageWithLayout = () => {
  const { me, currentPlan, currentCycle } = useMe();
  const walletPoints = me?.wallet?.available_points || 0;
  // const walletTrainingPoints = me?.wallet?.available_train_model_points || 0;
  const router = useRouter();

  const handleManageSubscription = async () => {
    try {
      const response = await Client.stripe.createPortalSession({
        return_url: window.location.href,
      });
      window.location.href = response.url;
    } catch (error) {
      toast.error('Failed to open subscription management portal');
    }
  };

  const scrollToPricingPlans = (tab?: string) => {
    document
      .getElementById('pricing-plans')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="flex w-full flex-col">
      <div className="my-10 flex w-full flex-col p-4 sm:my-10 lg:px-40">
        <div className="mx-auto w-full max-w-[1200px]">
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col space-y-2">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Manage Your Account
                </h1>
                <p className="text-muted-foreground text-sm">
                  Manage your subscription and billing information
                </p>
              </div>
              {currentPlan !== 'Free Plan' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleManageSubscription}
                    className="flex-1 hover:bg-primary hover:text-primary-foreground sm:flex-none"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Cancel Plan
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      scrollToPricingPlans('subscription');
                      router.push(`#subscription`);
                    }}
                    className="flex-1 hover:bg-primary hover:text-primary-foreground sm:flex-none"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Change Plan
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      scrollToPricingPlans('credits');
                      router.push(`#credits`);
                    }}
                    className="flex-1 hover:bg-primary hover:text-primary-foreground sm:flex-none"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Buy Credits
                  </Button>
                </div>
              )}
            </div>

            <Card className="bg-card border-border p-4 sm:p-6">
              <div className="flex flex-col gap-6">
                <div className="flex-1 space-y-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-primary" />
                      <span className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                        {currentPlan}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {currentPlan === 'Free Plan'
                        ? 'Upgrade to unlock premium features and increased limits'
                        : 'Your subscription is active and will renew automatically ' +
                          (currentCycle === 'month'
                            ? 'each month'
                            : 'each year')}
                    </p>
                  </div>
                </div>

                <div className="flex-1 space-y-3">
                  <h3 className="text-sm font-medium text-foreground">
                    Available Credits:
                  </h3>
                  <div className="grid grid-cols-1 gap-4 xs:grid-cols-2">
                    <div className="flex items-center rounded-lg bg-primary/5 p-3 transition-colors">
                      <div className="group relative mr-3">
                        <div className="absolute inset-[-2px] rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/30 opacity-75 blur-sm"></div>
                        <Image
                          src={Pbucks}
                          width={10}
                          height={10}
                          layout="responsive"
                          alt="P-Bucks"
                          className="relative !h-5 !w-5 animate-[pulseBorder_2s_infinite] duration-300 transition-transform group-hover:scale-110"
                        />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          P-Bucks (Credits for AI Models)
                        </p>
                        <p className="text-lg font-semibold">{walletPoints}</p>
                      </div>
                    </div>

                    {/* <div className="flex items-center rounded-lg bg-primary/5 p-3 transition-colors">
                      <DiamondIcon className="mr-2 h-4 w-4" />
                      <div>
                        <p className="text-muted-foreground text-xs">
                          Loyalty Points
                        </p>
                        <p className="text-lg font-semibold">
                          {walletTrainingPoints}
                        </p>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <PricingLayout buttonLabel="Change Plan" />
    </div>
  );
};

Billing.authorization = true;
Billing.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default Billing;

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['common', 'table'])),
      revalidate: 60, // In seconds
    },
  };
};
